import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { SelectProps } from "@mui/material/Select";
import { CSSProperties } from "@mui/styles";
import { getKey } from "@recare/core/model/utils/strings";
import {
  FieldValidation,
  FormElementRenderProps,
  isValid,
} from "@recare/react-forms-state";
import { useTranslations } from "@recare/translations";
import { TEXT_DARK_PRIMARY } from "ds/materials/colors";
import { FONT_SIZE_14 } from "ds/materials/typography";
import { useEffect, useState } from "react";

export type SelectOption = {
  id: number | string;
  label: number | string;
  value: number | string;
};

export type SelectInputProps<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = undefined,
> = Pick<
  SelectProps,
  "disabled" | "id" | "label" | "placeholder" | "required" | "size" | "variant"
> & {
  elementName: string;
  multiple?: Multiple;
  nullable?: Multiple extends true ? never : Nullable;
  onChange: (
    value: Multiple extends true
      ? Option["value"][]
      : Nullable extends true
      ? Option["value"] | null
      : Option["value"],
    statePath?: string | undefined,
  ) => void;
  options: Option[];
  style?: CSSProperties;
  validation?: FieldValidation | undefined;
  value: Multiple extends true ? Option["value"][] : Option["value"] | null;
};

export type ConnectedSelectInputProps<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = undefined,
> = Omit<
  SelectInputProps<Option, Multiple, Nullable>,
  "onChange" | "validation" | "value"
> & { sideMutation?: (newValue: any, mutateElement: AnyFunction) => void };

export function optionToSelectOption<
  Option extends SelectOption = SelectOption,
>(option: unknown): Option {
  return { value: option, id: option, label: option } as unknown as Option;
}

export function optionsToSelectOptions<
  Option extends SelectOption = SelectOption,
>(options: unknown[]): Option[] {
  return options.map(optionToSelectOption) as unknown as Option[];
}

const labelStyle: CSSProperties = {
  fontSize: FONT_SIZE_14,
  color: TEXT_DARK_PRIMARY,
};

export function SelectInput<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = undefined,
>({
  disabled,
  elementName,
  id,
  label,
  multiple,
  nullable,
  onChange,
  options,
  placeholder,
  required,
  size,
  style,
  validation,
  value,
  variant = "standard",
}: SelectInputProps<Option, Multiple, Nullable>) {
  const translations = useTranslations();
  const [localValue, setValue] = useState<Option["value"] | Option["value"][]>(
    multiple ? [] : "",
  );
  const defaultId = id || elementName;

  const handleChange = (event: SelectChangeEvent<typeof localValue>) => {
    const { value } = event.target;

    setValue(value);

    onChange(
      (multiple
        ? value
        : `${value}`?.length
        ? value
        : null) as Multiple extends true
        ? string[]
        : Nullable extends true
        ? string | null
        : string,
    );
  };

  useEffect(() => {
    setValue(
      (value != null ? value : multiple ? [] : "") as
        | Option["value"]
        | Option["value"][],
    );
  }, [value]);

  return (
    <FormControl
      disabled={disabled}
      error={!isValid(validation)}
      fullWidth
      required={required}
      sx={{
        ...labelStyle,
        ...style,
        "& .MuiSelect-select": labelStyle,
        "& .MuiFormLabel-root": labelStyle,
      }}
      size={size}
      variant={variant}
    >
      <InputLabel id={`${defaultId}-label`} data-testid={`${defaultId}-label`}>
        {label}
      </InputLabel>
      <Select
        id={defaultId}
        data-testid={defaultId}
        label={label}
        labelId={`${defaultId}-label`}
        multiple={multiple}
        name={elementName}
        onChange={handleChange}
        placeholder={placeholder}
        renderValue={
          multiple
            ? (selected) => {
                const selectedOptions = options.filter((option) =>
                  (selected as string[]).includes(option.value as string),
                );
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectedOptions?.map(({ label, value }) => (
                      <Chip key={value} label={label} />
                    ))}
                  </div>
                );
              }
            : undefined
        }
        value={localValue}
      >
        {nullable && !multiple && (
          <MenuItem
            data-testid="option-none"
            divider
            key={getKey("none")}
            sx={labelStyle}
            value={""}
          >
            <em>{translations.general.noPreference}</em>
          </MenuItem>
        )}
        {options.map(({ label, value }) => {
          return (
            <MenuItem
              data-testid={`option-${value}`}
              divider
              key={value}
              sx={labelStyle}
              value={value}
            >
              {multiple ? (
                <>
                  <Checkbox
                    checked={
                      (localValue as Option["value"][]).indexOf(value) > -1
                    }
                    size="small"
                    sx={labelStyle}
                  />
                  <span style={labelStyle as any}>{label}</span>
                </>
              ) : (
                label
              )}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export default function ConnectedSelectInput<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = undefined,
>({
  disabled,
  elementName,
  id,
  label,
  multiple,
  nullable,
  options,
  placeholder,
  required,
  sideMutation,
  variant = "standard",
}: Omit<
  SelectInputProps<Option, Multiple, Nullable>,
  "onChange" | "validation" | "value"
> & {
  sideMutation?: (newValue: any, mutateElement: AnyFunction) => void;
}) {
  return (
    <FormElementRenderProps
      elementName={elementName}
      sideMutation={sideMutation}
    >
      {({ onChange, validation, value }) => (
        <SelectInput<Option, Multiple, Nullable>
          disabled={disabled}
          elementName={elementName}
          id={id}
          label={label}
          multiple={multiple}
          nullable={nullable}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          required={required}
          validation={validation}
          value={value}
          variant={variant}
        />
      )}
    </FormElementRenderProps>
  );
}
